import type { Arquivo } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { GaleriaApi } from './galeria.api.js';

const MODULOS_CONHECIDOS = ['cms', 'clientes', 'producao', 'parceiros'] as const;

/**
 * `<erp-upload-arquivo-modal>` — envia um arquivo real para o Storage
 * (Sprint 12). Área de arrastar-e-soltar + seleção por clique, mesmo
 * padrão de "um único ponto de escolha" dos demais modais de criação
 * (`<erp-movimentacao-form-modal>` etc). `module` é obrigatório porque
 * é ele que define o contexto de validação real no backend (MIME/
 * tamanho máximo, `storage.service.ts`) — nunca inventado, sempre uma
 * escolha explícita de quem envia.
 */
export class ErpUploadArquivoModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _galeriaApi: GaleriaApi | null = null;
  private _aoSalvar: ((arquivo: Arquivo) => void) | null = null;

  private aberto = false;
  private arquivoSelecionado: File | null = null;
  private arrastando = false;
  private moduloSelecionado: string = MODULOS_CONHECIDOS[0];
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set galeriaApi(valor: GaleriaApi) {
    this._galeriaApi = valor;
  }

  set aoSalvar(valor: (arquivo: Arquivo) => void) {
    this._aoSalvar = valor;
  }

  abrir(): void {
    this.arquivoSelecionado = null;
    this.arrastando = false;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private selecionarArquivo(arquivo: File | undefined | null): void {
    if (!arquivo) return;
    this.arquivoSelecionado = arquivo;
    this.mensagemErro = '';
    this.render();
  }

  private lerCamposForm(): { folder: string; tags: string; isPublic: boolean } {
    return {
      folder: this.root.querySelector<HTMLInputElement>('#folder')?.value.trim() ?? '',
      tags: this.root.querySelector<HTMLInputElement>('#tags')?.value.trim() ?? '',
      isPublic: this.root.querySelector<HTMLInputElement>('#is-public')?.checked ?? false,
    };
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._galeriaApi || this.enviando) return;

    if (!this.arquivoSelecionado) {
      this.mensagemErro = 'selecione um arquivo';
      this.render();
      return;
    }

    const { folder, tags, isPublic } = this.lerCamposForm();
    const tagsLista = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const arquivo = await this._galeriaApi.upload({
        arquivo: this.arquivoSelecionado,
        module: this.moduloSelecionado,
        ...(folder ? { folder } : {}),
        ...(tagsLista.length > 0 ? { tags: tagsLista } : {}),
        isPublic,
      });
      this.aberto = false;
      this._aoSalvar?.(arquivo);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível enviar o arquivo.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .checkbox { flex-direction: row; align-items: center; gap: var(--pg-space-3, 12px); }
        .checkbox input { width: auto; }
        .dropzone {
          border: 2px dashed var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          padding: var(--pg-space-6, 32px);
          text-align: center;
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          cursor: pointer;
          transition: border-color 0.15s ease, color 0.15s ease;
        }
        .dropzone:hover { border-color: var(--pg-color-texto-desabilitado, #5c626b); }
        .dropzone.arrastando { border-color: var(--pg-color-destaque, #2f8cff); color: var(--pg-color-destaque, #2f8cff); }
        .dropzone.selecionado { border-style: solid; border-color: var(--pg-color-sucesso, #3bb273); color: var(--pg-color-texto-primario, #f2f3f5); }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Enviar arquivo</span>
        <form novalidate>
          <div class="dropzone ${this.arrastando ? 'arrastando' : ''} ${this.arquivoSelecionado ? 'selecionado' : ''}" id="dropzone">
            ${
              this.arquivoSelecionado
                ? `${escaparHtml(this.arquivoSelecionado.name)} (${formatarTamanho(this.arquivoSelecionado.size)})`
                : 'Arraste um arquivo aqui, ou clique para selecionar'
            }
          </div>
          <input id="input-arquivo" type="file" style="display:none" />

          <label>
            Módulo (contexto de validação)
            <select id="modulo">
              ${MODULOS_CONHECIDOS.map((m) => `<option value="${m}" ${m === this.moduloSelecionado ? 'selected' : ''}>${m}</option>`).join('')}
            </select>
          </label>
          <label>Pasta (opcional) <input id="folder" type="text" maxlength="128" /></label>
          <label>Tags (opcional, separadas por vírgula) <input id="tags" type="text" maxlength="200" /></label>
          <label class="checkbox"><input id="is-public" type="checkbox" /> Arquivo público</label>

          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Enviar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());

    this.root.querySelector('#modulo')?.addEventListener('change', (evento) => {
      this.moduloSelecionado = (evento.target as HTMLSelectElement).value;
    });

    const inputArquivo = this.root.querySelector<HTMLInputElement>('#input-arquivo');
    const dropzone = this.root.querySelector('#dropzone');
    dropzone?.addEventListener('click', () => inputArquivo?.click());
    inputArquivo?.addEventListener('change', () => this.selecionarArquivo(inputArquivo.files?.[0]));

    dropzone?.addEventListener('dragover', (evento) => {
      evento.preventDefault();
      this.arrastando = true;
      this.render();
    });
    dropzone?.addEventListener('dragleave', () => {
      this.arrastando = false;
      this.render();
    });
    dropzone?.addEventListener('drop', (evento) => {
      evento.preventDefault();
      this.arrastando = false;
      const arquivo = (evento as DragEvent).dataTransfer?.files?.[0];
      this.selecionarArquivo(arquivo);
    });
  }
}

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-upload-arquivo-modal', ErpUploadArquivoModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-upload-arquivo-modal': ErpUploadArquivoModal;
  }
}
